import { useMemo } from 'react';
import { isMilestoneNode } from '../utils/milestoneUtils';

/**
 * useEffectiveChainInfo Hook
 * 
 * Recalculates chain positions based on actual nodePositions (includes dragged positions).
 * This ensures chain headers follow when nodes are dragged.
 * Only considers tasks at the TOPMOST Y position for the brace width.
 * 
 * When filtering is active, only considers VISIBLE tasks for width calculation.
 * This ensures the brace spans only the visible top-row nodes, not lower rows.
 * 
 * Also calculates effectiveOrder based on actual X positions, so that the
 * left/right navigation buttons are correctly enabled/disabled after tidy
 * operations that may reorder chains visually.
 */
export function useEffectiveChainInfo({
  tasks,
  chainInfo,
  nodePositions,
  nodeDimensions,
}) {
  return useMemo(() => {
    if (!chainInfo || chainInfo.length === 0) return [];
    
    // Build a set of visible task IDs (tasks that are currently rendered)
    const visibleTaskIds = new Set(tasks.map(t => t.id));
    const milestoneIds = new Set(tasks.filter((t) => isMilestoneNode(t) || t.nodeType === 'note').map(t => t.id));
    
    // First pass: calculate positions for all chains
    const chainsWithPositions = chainInfo.map(chain => {
      // Filter to visible mission/decision tasks only (milestones never affect brace width)
      const visibleChainTaskIds = chain.taskIds.filter(
        id => visibleTaskIds.has(id) && !milestoneIds.has(id),
      );
      
      // If no visible tasks in this chain, skip it (return null, filter later)
      if (visibleChainTaskIds.length === 0) {
        return null;
      }
      
      // First pass: find the minimum Y position (topmost row) among visible tasks
      let minY = Infinity;
      visibleChainTaskIds.forEach(taskId => {
        const pos = nodePositions[taskId];
        // Skip invalid positions (null, undefined, NaN, Infinity)
        if (pos && Number.isFinite(pos.x) && Number.isFinite(pos.y)) {
          minY = Math.min(minY, pos.y);
        }
      });
      
      // If no valid positions found, use original chain info
      if (minY === Infinity) {
        return chain;
      }
      
      // Second pass: find tasks at the topmost Y position (with small tolerance for rounding)
      const Y_TOLERANCE = 5; // Allow small variance in Y positions
      const topRowTaskIds = visibleChainTaskIds.filter(taskId => {
        const pos = nodePositions[taskId];
        return pos && Number.isFinite(pos.y) && Math.abs(pos.y - minY) <= Y_TOLERANCE;
      });
      
      // Calculate width from top row tasks only
      let minX = Infinity;
      let maxX = -Infinity;
      
      topRowTaskIds.forEach(taskId => {
        const pos = nodePositions[taskId];
        const dims = nodeDimensions[taskId] || { width: 288, height: 100 };
        // Skip invalid positions (null, undefined, NaN, Infinity)
        if (pos && Number.isFinite(pos.x) && Number.isFinite(pos.y)) {
          minX = Math.min(minX, pos.x);
          maxX = Math.max(maxX, pos.x + dims.width);
        }
      });
      
      // If no valid positions found for top row, use original
      if (minX === Infinity) {
        return chain;
      }
      
      return {
        ...chain,
        position: { x: minX, y: minY },
        width: maxX - minX,
      };
    }).filter(Boolean); // Remove null entries (chains with no visible tasks)
    
    // Second pass: calculate effectiveOrder based on actual X positions
    // Sort chains by their X position to determine visual order
    const sortedByX = [...chainsWithPositions].sort((a, b) => a.position.x - b.position.x);
    
    // Create a map of chainId -> effectiveOrder
    const effectiveOrderMap = new Map();
    sortedByX.forEach((chain, index) => {
      effectiveOrderMap.set(chain.id, index);
    });
    
    // Add effectiveOrder to each chain
    return chainsWithPositions.map(chain => ({
      ...chain,
      effectiveOrder: effectiveOrderMap.get(chain.id),
    }));
  }, [chainInfo, nodePositions, nodeDimensions, tasks]);
}

export default useEffectiveChainInfo;
